import { useState } from "react";
import { Badge } from "@component/badge";
import {
    ChevronDownIcon,
    ChevronUpIcon,
    AlertTriangleIcon,
    InfoIcon,
} from "lucide-react";
import { cn } from "@util/tailwind";
import { Tooltip } from "@component/tooltip";

export interface ScoreBreakdownItem {
    category: string;
    score: number;
    maxScore: number;
    remarks: string;
    mustHaves?: { label: string; matched: boolean; explanation?: string }[];
    hasFlag?: boolean;
    flagText?: string;
    sourceRemarks?: { remark: string }[];
}

export interface ScoreSubSection {
    title: string;
    percentage: string;
    score: number;
    remarks?: string;
    items?: ScoreBreakdownItem[];
    maxScore?: number;
    /** Show a small loader in this sub-section's item area while its breakdown
     *  rows are still loading (the score/title above render immediately). */
    isLoading?: boolean;
}

interface ScoreSectionCollapsibleProps {
    title: string;
    subtitle?: string;
    score?: number;
    maxScore?: number;
    icon?: React.ReactNode;
    iconBgColor?: string;
    items: ScoreBreakdownItem[];
    defaultExpanded?: boolean;
    isScored?: boolean;
    badge?: React.ReactNode;
    onShowMustHaves?: (mustHaves: { label: string; matched: boolean; explanation?: string }[]) => void;
    subSections?: ScoreSubSection[];
    completedDate?: string;
    completedTime?: string;
    stage?: string;
    /** Show a loader in the expanded body while this section's data is still
     *  loading, without blocking the header (score/stage render immediately). */
    isLoading?: boolean;
}

/** Placeholder rows for a breakdown table/sub-section still loading its data —
 *  a skeleton shaped like the real rows (category · score chip · remarks) so the
 *  loading state is consistent across the CV and Assessment breakdowns. */
function BreakdownSkeleton() {
    return (
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {[0, 1, 2].map((i) => (
                <div
                    key={i}
                    className="grid grid-cols-[minmax(200px,0.4fr)_minmax(80px,auto)_2.5fr] gap-6 py-4"
                    style={{ paddingLeft: "12px", paddingRight: "16px" }}
                >
                    <div className="h-4 w-3/4 rounded bg-gray-100 dark:bg-gray-800 animate-pulse" />
                    <div className="h-6 w-16 rounded-full bg-gray-100 dark:bg-gray-800 animate-pulse" />
                    <div className="space-y-2">
                        <div className="h-3 w-full rounded bg-gray-100 dark:bg-gray-800 animate-pulse" />
                        <div className="h-3 w-5/6 rounded bg-gray-100 dark:bg-gray-800 animate-pulse" />
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function ScoreSectionCollapsible({
    title,
    subtitle,
    score,
    maxScore = 100,
    icon,
    iconBgColor = "bg-blue-100 dark:bg-blue-900/30",
    items,
    defaultExpanded = true,
    isScored = true,
    badge,
    onShowMustHaves,
    subSections,
    completedDate,
    completedTime,
    stage,
    isLoading = false,
}: ScoreSectionCollapsibleProps) {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    const getScoreChipColor = (score: number, maxScore: number) => {
        const percentage = (score / maxScore) * 100;
        if (percentage >= 80)
            return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
        if (percentage >= 60)
            return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
    };

    return (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-900">
            {/* Header */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 hover:shadow-sm transition-all"
            >
                <div className="flex items-center gap-3">
                    {/* Icon */}
                    {icon && (
                        <div
                            className={cn(
                                "w-10 h-10 rounded-lg flex items-center justify-center",
                                iconBgColor
                            )}
                        >
                            {icon}
                        </div>
                    )}

                    {/* Title and Score Chip */}
                    <div className="flex items-center gap-3">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                            {title}
                        </h3>
                        {/* Badge or Score */}
                        {badge ? (
                            badge
                        ) : isScored && score !== undefined ? (
                            <Badge
                                className={`${getScoreChipColor(score, maxScore)} px-4 py-1.5 rounded-full shadow-none pointer-events-none text-base font-semibold`}
                            >
                                {score}/{maxScore}
                            </Badge>
                        ) : null}
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    {/* Stage and Completed Details */}
                    {completedDate && completedTime && stage ? (
                        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                            <span><span className="font-semibold">Stage:</span> {stage}</span>
                            <span>•</span>
                            <span><span className="font-semibold">Completed on:</span> {completedDate} at {completedTime}</span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                            {stage && <span><span className="font-semibold">Stage:</span> {stage}</span>}
                            {stage && completedDate && completedTime && <span>•</span>}
                            {completedDate && completedTime && (
                                <span><span className="font-semibold">Completed on:</span> {completedDate} at {completedTime}</span>
                            )}
                            {!completedDate && !completedTime && !stage && subtitle && (
                                <span>{subtitle}</span>
                            )}
                        </div>
                    )}

                    {/* Expand/Collapse Icon */}
                    {isExpanded ? (
                        <ChevronUpIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                    ) : (
                        <ChevronDownIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                    )}
                </div>
            </button>

            {/* Content */}
            {isExpanded && (
                <div className="border-t border-gray-200 dark:border-gray-700">
                    {/* Loading state — data for this section is still being fetched */}
                    {isLoading ? (
                        <BreakdownSkeleton />
                    ) : subSections && subSections.length > 0 ? (
                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                            {subSections.map((subSection, subIdx) => (
                                <div key={subIdx} className="py-4 px-4">
                                    {/* SubSection Header */}
                                    <div className="mb-4">
                                        <div className="grid grid-cols-[minmax(200px,0.4fr)_minmax(80px,auto)_2.5fr] gap-6">
                                            <div>
                                                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                    {subSection.title}
                                                </h4>
                                                {subSection.percentage !== "" && (
                                                    <span className="text-sm text-muted-foreground">
                                                        ({subSection.percentage} weightage)
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-start">
                                                <Badge
                                                    className={`${getScoreChipColor(subSection.score, 100)} px-3 py-1 rounded-full shadow-none pointer-events-none`}
                                                >
                                                    {subSection.score}/{subSection.maxScore || 100}
                                                </Badge>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                {subSection.remarks && (
                                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                                        {subSection.remarks}
                                                    </div>
                                                )}
                                                {/* {subSection.title === "Form" && (
                                                    <button className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-black dark:hover:text-white transition-colors ml-auto underline">
                                                        View Detailed Form Score
                                                    </button>
                                                )} */}
                                            </div>
                                        </div>
                                    </div>

                                    {/* SubSection Items — a skeleton while just this
                                        sub-section's breakdown rows are still loading. */}
                                    {subSection.isLoading ? (
                                        <BreakdownSkeleton />
                                    ) : (subSection?.items || []).length > 0 && (
                                        <div className="divide-y divide-gray-200 dark:divide-gray-700 -mx-4">
                                            {(subSection?.items || []).map((item, itemIdx) => (
                                                <div
                                                    key={itemIdx}
                                                    className="grid gap-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 pr-4"
                                                    style={{
                                                        gridTemplateColumns: "minmax(200px, 0.4fr) minmax(80px, auto) 2.5fr",
                                                        paddingLeft: "calc(1rem + 32px)"
                                                    }}
                                                >
                                                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                        {item.category}
                                                    </div>
                                                    {isScored && (
                                                        <div className="flex items-start gap-2">
                                                            <Badge
                                                                className={`${getScoreChipColor(item.score, item.maxScore)} px-3 py-1 rounded-full shadow-none pointer-events-none`}
                                                            >
                                                                {item.score}/{item.maxScore}
                                                            </Badge>
                                                        </div>
                                                    )}
                                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                                        {item.remarks}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        /* Regular Table */
                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                            {items.map((item, index) => (
                                <div
                                    key={index}
                                    className="grid grid-cols-[minmax(200px,0.4fr)_minmax(80px,auto)_2.5fr] gap-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                                    style={{ paddingLeft: "12px", paddingRight: "16px" }}
                                >
                                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        {item.category === "Must-haves" ? (
                                            <div className="flex items-center gap-2">
                                                <span>{item.category}</span>
                                                <Tooltip content="These must-haves are extracted from your job description." side="right">
                                                    <InfoIcon className="w-4 h-4 text-gray-400 dark:text-gray-500 cursor-help" />
                                                </Tooltip>
                                            </div>
                                        ) : (
                                            item.category
                                        )}
                                    </div>
                                    {isScored ? (
                                        <div className="flex items-center gap-2">
                                            <Badge
                                                className={`${getScoreChipColor(item.score, item.maxScore)} px-3 py-1 rounded-full shadow-none pointer-events-none`}
                                            >
                                                {item.score}/{item.maxScore}
                                            </Badge>
                                        </div>
                                    ) : (
                                        <div></div>
                                    )}
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                        {item.category === "Must-haves" &&
                                            item.mustHaves &&
                                            item.mustHaves.length > 0 ? (
                                            <>
                                                    Candidate meets {item.mustHaves.filter((item) => item.matched).length} out of {item.mustHaves.length}{" "}
                                                    <button
                                                        onClick={() => {
                                                            if (onShowMustHaves && item.mustHaves) {
                                                                onShowMustHaves(item.mustHaves);
                                                            }
                                                        }}
                                                        className="text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300"
                                                    >
                                                        must-have
                                                    </button>{" "}
                                                    items from the job description.
                                            </>
                                        ) : item.sourceRemarks && item.sourceRemarks.length > 0 ? (
                                            <div className="leading-relaxed inline">
                                                {item.sourceRemarks.map((sourceRemark, remarkIdx) => (
                                                    <span key={remarkIdx}>
                                                        {sourceRemark.remark}
                                                        {remarkIdx < item.sourceRemarks!.length - 1 && " "}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <span>{item.remarks}</span>
                                        )}
                                        {!isScored && item.hasFlag && (
                                            <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800 px-3 py-1 rounded-full shadow-none pointer-events-none inline-flex items-center gap-1.5 ml-2">
                                                <AlertTriangleIcon className="w-3.5 h-3.5" />
                                                {item.flagText || "Flag identified"}
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
